import { launch, step, createNode, createEffect, createEvent } from 'effector';

const QUEUE = 'QUEUE';
const RACE = 'RACE';
const TAKE_EVERY = 'TAKE_EVERY';
const TAKE_FIRST = 'TAKE_FIRST';
const TAKE_LAST = 'TAKE_LAST';

class ReEffectError extends Error {
    constructor(message) {
        super(message);
        delete this.stack;
    }
}
class CancelledError extends ReEffectError {
    constructor(strategy = 'cancel') {
        super('Cancelled due to "' + strategy + '"' + ({
            [TAKE_FIRST]: `, there are already running effects`,
            [TAKE_LAST]: `, new effect was added`,
            [RACE]: `, other effect won race`,
            cancel: `, cancel all already running effects`,
        }[strategy] || `, but should not happen...`));
    }
}
class LimitExceededError extends ReEffectError {
    constructor(limit, running) {
        super('Cancelled due to limit of "' + limit + '" exceeded,' +
            'there are already ' + running + ' running effects');
    }
}
class TimeoutError extends ReEffectError {
    constructor(timeout) {
        super('Cancelled due to timeout of "' + timeout + '"ms');
    }
}

const assign = Object.assign;
const own = ({ graphite: owner }, links) => {
    for (const { graphite } of links) {
        graphite.family.type = 'crosslink';
        graphite.family.owners.push(owner);
        owner.family.links.push(graphite);
    }
};

const cancellable = (promise, abort, timeout) => {
    let cancel;
    const cancelable = new Promise((_, reject) => {
        let timeoutId;
        const rejectWith = Cls => arg => {
            clearTimeout(timeoutId);
            reject(new Cls(arg));
            abort && abort();
        };
        cancel = rejectWith(CancelledError);
        if (typeof timeout === 'number') {
            timeoutId = setTimeout(rejectWith(TimeoutError), timeout, timeout);
        }
    });
    return assign(Promise.race([promise, cancelable]), { cancel });
};
const defer = () => {
    const deferred = {};
    deferred.req = new Promise((resolve, reject) => {
        deferred.rs = resolve;
        deferred.rj = reject;
    });
    deferred.req.catch(() => { });
    return deferred;
};

const patchInstance = (instance, { cancelled, cancel, feedback }) => {
    assign(instance, { cancelled, cancel });
    own(instance, [cancelled, cancel]);
    instance.create = (paramsOrConfig, [strategyOrConfig]) => {
        const config = (paramsOrConfig && (paramsOrConfig.strategy || paramsOrConfig.timeout)
            ? paramsOrConfig
            : strategyOrConfig && (strategyOrConfig.strategy || strategyOrConfig.timeout)
                ? strategyOrConfig
                : { strategy: strategyOrConfig });
        const req = defer();
        launch(instance, {
            params: config === paramsOrConfig ? config.params : paramsOrConfig,
            args: {
                strategy: config.strategy,
                timeout: config.timeout,
            },
            req,
        });
        return req.req;
    };
    if (feedback) {
        const feedbackStep = step.compute({
            fn(data, _, stack) {
                data.strategy = stack.parent.parent.value.strategy;
                return data;
            },
        });
        instance.done.graphite.seq.push(feedbackStep);
        instance.fail.graphite.seq.push(feedbackStep);
    }
};

const patchRunner = (runner, scope) => {
    assign(runner.scope, scope);
    runner.meta.onCopy.push('cancelled', 'cancel');
    runner.seq = seq();
    scope.cancel.watch(() => scope.cancelAll());
};
const seq = () => [
    step.run({
        fn({ params, args, req }, { getHandler, finally: anyway, strategy, feedback, limit, timeout, cancelled, running, inFlight, push, unpush, cancelAll, }, { page }) {
            strategy = (args && args.strategy) || strategy;
            timeout = (args && args.timeout) || timeout;
            const scope = {
                params,
                strategy,
                timeout,
                feedback,
                push,
                unpush,
                cancelAll,
                inFlight,
                page,
            };
            const go = run(scope, getHandler(), fin(scope, anyway, 0, req.rs), fin(scope, anyway, 1, req.rj), fin(scope, cancelled, 2, req.rj));
            if (running.length >= limit) {
                return go(new LimitExceededError(limit, running.length));
            }
            if (strategy === TAKE_FIRST && running.length > 0) {
                return go(new CancelledError(strategy));
            }
            if (strategy === TAKE_LAST && running.length > 0) {
                cancelAll(strategy);
            }
            if (strategy === QUEUE && running.length > 0) {
                const promise = cancellable(Promise.all(running.map(p => p.catch(() => { }))));
                push(promise);
                return promise.then(() => (unpush(promise), go()), error => (unpush(promise), go(error)));
            }
            go();
        },
    }),
];
const run = ({ params, push, timeout }, handler, onResolve, onReject, onCancel) => (immediatelyCancelError) => {
    if (immediatelyCancelError) {
        return onCancel()(immediatelyCancelError);
    }
    let cancel;
    let result;
    try {
        result = handler(params, abort => (cancel = abort));
    }
    catch (error) {
        return onReject()(error);
    }
    if (Object(result) === result && typeof result.then === 'function') {
        const promise = cancellable(result, cancel, timeout);
        push(promise);
        return promise.then(onResolve(promise), error => error instanceof CancelledError
            ? onCancel(promise)(error)
            : onReject(promise)(error));
    }
    return onResolve()(result);
};
const fin = ({ params, unpush, strategy, feedback, cancelAll, inFlight, page, }, target, type, fn) => (promise) => (data) => {
    const runningCount = unpush(promise);
    const targets = [inFlight, sidechain];
    const payloads = [runningCount, [fn, data]];
    if (type === 2 || !runningCount || strategy === RACE) {
        const body = type === 0
            ? { params, result: data, status: 'done' }
            : type === 1
                ? { params, error: data, status: 'fail' }
                : { params, error: data };
        if (feedback)
            body.strategy = strategy;
        targets.unshift(target);
        payloads.unshift(body);
        if (strategy === RACE && type !== 2) {
            cancelAll(strategy);
        }
    }
    launch({
        target: targets,
        params: payloads,
        defer: true,
        page,
    });
};
const sidechain = createNode({
    node: [
        step.run({
            fn([fn, value]) {
                fn(value);
            },
        }),
    ],
    meta: { op: 'fx', fx: 'sidechain' },
});

const createReEffectFactory = (createEffect$1 = createEffect) => (nameOrConfig, maybeConfig) => {
    const instance = createEffect$1(nameOrConfig, maybeConfig);
    const cancelled = createEvent({ named: 'cancelled' });
    const cancel = createEvent({ named: 'cancel' });
    const config = maybeConfig
        ? maybeConfig
        : nameOrConfig && typeof nameOrConfig === 'object'
            ? nameOrConfig
            : {};
    const running = [];
    const scope = {
        strategy: config.strategy || TAKE_EVERY,
        feedback: config.feedback || false,
        limit: config.limit || Infinity,
        timeout: config.timeout,
        cancelled,
        cancel,
        running,
        inFlight: instance.inFlight,
        push: (promise) => running.push(promise),
        unpush: (promise) => {
            if (promise) {
                running.splice(running.indexOf(promise), 1);
            }
            return running.length;
        },
        cancelAll: (strategy) => running.map(promise => promise.cancel(strategy)),
    };
    patchRunner(instance.graphite.scope.runner, scope);
    patchInstance(instance, scope);
    return instance;
};

const createReEffect = createReEffectFactory();

export { CancelledError, LimitExceededError, QUEUE, RACE, ReEffectError, TAKE_EVERY, TAKE_FIRST, TAKE_LAST, TimeoutError, createReEffect, createReEffectFactory };
//# sourceMappingURL=index.js.map
